import { useEffect, useState } from "react";
import client from "@/lib/api";
import { Coins } from "lucide-react";

// Static fallback so components render before /usage/me returns
const DEFAULT_COSTS = {
    audit: 5, brief: 5, digest: 3, rank_explainer: 2,
    bulk_diagnose_base: 3, bulk_diagnose_per_kw: 1,
    content_analyze: 1, disavow_export: 0,
};

let _cache = null;

/**
 * Hook: returns the CREDIT_COSTS map from the backend (cached).
 */
export function useCreditCosts() {
    const [costs, setCosts] = useState(_cache || DEFAULT_COSTS);
    useEffect(() => {
        if (_cache) return;
        client.get("/usage/me")
            .then((r) => {
                const c = r.data?.action_costs;
                if (c) { _cache = c; setCosts(c); }
            })
            .catch(() => { /* keep default */ });
    }, []);
    return costs;
}

/**
 * Inline pill: "· 5 credits" or "· 3+N credits" for variable-cost actions.
 * Small, minimal footprint — designed to sit next to a button label or icon.
 */
export function CreditCost({ action, extra = 0, size = "xs", className = "" }) {
    const costs = useCreditCosts();
    const base = costs[action] ?? DEFAULT_COSTS[action] ?? 0;
    const total = base + Number(extra || 0);
    const label = extra ? `${base}+${extra} credits` : `${total} credit${total === 1 ? "" : "s"}`;
    const font = size === "xs" ? "text-[10px]" : "text-xs";
    return (
        <span
            data-testid={`credit-cost-${action}`}
            className={`inline-flex items-center gap-0.5 font-mono ${font} text-muted-foreground ${className}`}
            title={`This action costs ${label}`}
        >
            <Coins className="w-2.5 h-2.5 opacity-80" />
            {label}
        </span>
    );
}
